import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaUser, FaBookmark, FaShare } from 'react-icons/fa';
import Footer from '../components/common/Footer';

const BlogPost = () => {
  const { id } = useParams();

  const blogPosts = {
    1: {
      id: 1,
      title: "Agentic AI and the Evolving Role of Leadership: Collaborating with Autonomous Intelligence",
      author: "Arun Kumar",
      date: "June 5, 2024",
      readTime: "2 min read",
      content: [
        {
          type: 'paragraph',
          text: "As artificial intelligence advances from reactive systems to autonomous, agentic AI, we're entering a new era of human-machine collaboration — one that challenges traditional leadership models and calls for bold, adaptive strategies. But what exactly is Agentic AI?"
        },
        {
          type: 'heading',
          level: 2,
          text: "What Is Agentic AI?"
        },
        {
          type: 'paragraph',
          text: "Agentic AI refers to systems that not only execute tasks but demonstrate goal-directed behavior, make context-aware decisions, and initiate actions to fulfill objectives, often without constant human prompts."
        },
        {
          type: 'paragraph',
          text: "Think of AI agents that:"
        },
        {
          type: 'list',
          items: [
            "Proactively manage customer support",
            "Automate supply chain optimizations in real time",
            "Orchestrate software deployments",
            "Or even act as strategic planning assistants"
          ]
        },
        {
          type: 'paragraph',
          text: "Unlike narrow AI tools, agentic systems have autonomy, memory, intent modeling, and often, multi-step reasoning — making them collaborators, not just tools."
        },
        {
          type: 'heading',
          level: 2,
          text: "Why Leadership Matters More Than Ever"
        },
        {
          type: 'paragraph',
          text: "With Agentic AI, the nature of work, decisions, and strategy is shifting. Leaders must evolve their mindset from managing tasks to curating outcomes and aligning AI with organizational purpose."
        },
        {
          type: 'heading',
          level: 3,
          text: "1. Define the Guardrails"
        },
        {
          type: 'paragraph',
          text: "Agentic systems require clear ethical boundaries, governance models, and acceptable trade-offs. Leadership must take the wheel in:"
        },
        {
          type: 'list',
          items: [
            "Setting AI alignment with company values",
            "Ensuring explainability and transparency",
            "Balancing automation with empathy"
          ]
        },
        {
          type: 'heading',
          level: 3,
          text: "2. Empower AI + Human Synergy"
        },
        {
          type: 'paragraph',
          text: "Rather than fear replacement, visionary leaders embrace co-agency. Agentic AI becomes a multiplier of human talent:"
        },
        {
          type: 'list',
          items: [
            "Leaders become orchestrators of AI-human collaboration",
            "Teams shift from routine execution to higher-order creativity and judgment"
          ]
        },
        {
          type: 'heading',
          level: 3,
          text: "3. Build Organizational Readiness"
        },
        {
          type: 'paragraph',
          text: "Agentic AI adoption isn't just about tech; it's about change management:"
        },
        {
          type: 'list',
          items: [
            "Reskilling teams to work with AI agents",
            "Redesigning roles and responsibilities",
            "Creating a culture of experimentation and responsible autonomy"
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: "From Automation to Augmentation to Agency"
        },
        {
          type: 'paragraph',
          text: "We're at a pivotal moment in the AI maturity curve:"
        },
        {
          type: 'paragraph',
          text: "Automation → Augmentation → Agency"
        },
        {
          type: 'paragraph',
          text: "Each step requires greater leadership involvement, not less."
        },
        {
          type: 'heading',
          level: 2,
          text: "Leadership Questions to Ask Today:"
        },
        {
          type: 'list',
          items: [
            "Are our teams ready to co-work with autonomous agents?",
            "What decisions do we delegate to AI — and which must remain human?",
            "How do we foster trust, control, and creativity in an AI-augmented workforce?"
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: "Final Thoughts"
        },
        {
          type: 'paragraph',
          text: "Agentic AI has the potential to redefine productivity, innovation, and leadership — but only if guided with clarity, ethics, and foresight."
        },
        {
          type: 'paragraph',
          text: "The future isn't about machines replacing leaders — it's about leaders shaping a future where AI becomes a trusted partner in progress."
        }
      ],
      authorBio: {
        name: "Arun Kumar",
        bio: "Technology enthusiast and thought leader in AI and digital transformation."
      }
    },
    2: {
      id: 2,
      title: "MLOps at Scale: Engineering the Invisible Backbone of Production AI",
      author: "Arun Kumar",
      date: "May 24, 2024",
      readTime: "8 min read",
      content: [
        {
          type: 'paragraph',
          text: "As machine learning matures from experimentation to real-world deployment, MLOps has become the critical foundation for any organization serious about AI. But here's the nuance: MLOps isn't just CI/CD for ML. It's about managing entropy in highly dynamic, data-driven systems."
        },
        {
          type: 'heading',
          level: 2,
          text: "The Complexity Behind Production ML"
        },
        {
          type: 'paragraph',
          text: "In traditional software engineering, once code is deployed, it behaves consistently until the next update. This is not true for ML systems. They are probabilistic, data-dependent, and degrade over time due to data drift, concept drift, and real-world feedback loops."
        },
        {
          type: 'paragraph',
          text: "MLOps addresses this through systems thinking and automation, encompassing:"
        },
        {
          type: 'list',
          items: [
            "Automated Data & Feature Pipelines",
            "Training pipelines with lineage tracking",
            "Model registries with promotion workflows",
            "Canary/Shadow deployments for ML",
            "Monitoring pipelines for performance, drift, bias, and fairness",
            "Continuous retraining and rollout governance"
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: "Beyond the Basics: Scaling MLOps"
        },
        {
          type: 'paragraph',
          text: "Here are five challenges that emerge as MLOps scales:"
        },
        {
          type: 'heading',
          level: 3,
          text: "1. Orchestration at the Edge and Across Clouds"
        },
        {
          type: 'paragraph',
          text: "You're not just deploying to a cloud endpoint anymore — you're deploying to edge devices, multi-region clusters, and hybrid clouds. This requires tight orchestration across TensorRT, ONNX, Kubernetes, and edge-optimized runtimes."
        },
        {
          type: 'heading',
          level: 3,
          text: "2. Model Lifecycle Governance"
        },
        {
          type: 'paragraph',
          text: "It's not enough to version a model. You need audibility across data, code, hyperparameters, infra, and predictions — especially in finance, healthcare, and legal domains. Think of it as GitOps + ModelOps + DataOps rolled into one."
        },
        {
          type: 'heading',
          level: 3,
          text: "3. Real-Time Model Monitoring"
        },
        {
          type: 'paragraph',
          text: "Static dashboards don't cut it. Advanced MLOps stacks now embed anomaly detection, alerting for skew, latency, and quality degradation, and trigger retraining pipelines autonomously when thresholds are breached."
        },
        {
          type: 'heading',
          level: 3,
          text: "4. Cross-functional Collaboration at Scale"
        },
        {
          type: 'paragraph',
          text: "Your MLOps architecture must support teams across domains: data scientists, ML engineers, SREs, compliance, and business. Templatized pipelines, reusable artifacts, and access control become non-negotiable."
        },
        {
          type: 'heading',
          level: 3,
          text: "5. Responsible AI Integration"
        },
        {
          type: 'paragraph',
          text: "Bias detection, explainability (SHAP/LIME), and ethical guardrails aren't afterthoughts — they're first-class citizens in the MLOps lifecycle."
        },
        {
          type: 'heading',
          level: 2,
          text: "MLOps is not a Tool. It's a Culture + Platform Strategy."
        },
        {
          type: 'paragraph',
          text: "Organizations often ask, \"What tool should we use — MLflow, SageMaker, KubeFlow, Airflow?\" The better question is:"
        },
        {
          type: 'paragraph',
          text: "How do we design for modularity, composability, and observability across the entire ML lifecycle?"
        },
        {
          type: 'paragraph',
          text: "Tooling should be pluggable, aligned to your maturity curve, and evolve with your infrastructure and governance needs."
        },
        {
          type: 'heading',
          level: 2,
          text: "Final Thought"
        },
        {
          type: 'paragraph',
          text: "MLOps is where software engineering, data engineering, and AI converge. It's invisible when it works — but it's what makes intelligent systems reliable, repeatable, and accurate."
        },
        {
          type: 'paragraph',
          text: "Let's stop treating MLOps as a post-model add-on. It is core infrastructure for intelligent enterprises."
        },
        {
          type: 'paragraph',
          text: "Would love to hear how others are scaling MLOps in regulated, distributed, or high-stakes environments."
        }
      ],
      authorBio: {
        name: "Arun Kumar",
        bio: "Technology enthusiast and thought leader in AI and digital transformation."
      },
      tags: ["MLOps", "EnterpriseAI", "ModelOps", "AIOps", "DataScience", "MLEngineering", "ResponsibleAI", "DevOpsForML"]
    },
    3: {
      id: 3,
      title: "Unlocking the Future of Personalized Healthcare: An AI Revolution",
      author: "Arun Kumar BS",
      credentials: "Doctoral in Business Administration [Emerging technologies — Gen AI], Golden Gate University [San Francisco]",
      date: "May 15, 2024",
      readTime: "10 min read",
      content: [
        {
          type: 'paragraph',
          text: "Artificial Intelligence (AI) has rapidly transformed industries worldwide, and healthcare is no exception. With the advent of personalized healthcare solutions, AI is redefining how patients, providers, and developers interact with medical systems. This blog explores the challenges, opportunities, and ethical considerations in building an AI-powered Personalized Healthcare Assistant that addresses the needs of diverse stakeholders."
        },
        {
          type: 'heading',
          level: 2,
          text: "Why Personalized Healthcare Matters"
        },
        {
          type: 'paragraph',
          text: "Healthcare is not one-size-fits-all. Patients have unique symptoms, medical histories, and health goals that demand tailored solutions. Traditional healthcare systems often fail to deliver this level of personalization, leaving gaps in chronic condition management, fitness tracking, and lifestyle improvement."
        },
        {
          type: 'paragraph',
          text: "Enter the Personalized Healthcare Assistant, a groundbreaking platform that leverages AI to:"
        },
        {
          type: 'list',
          items: [
            "Empower Patients: Provide real-time, tailored health advice.",
            "Support Healthcare Providers: Offer data-driven insights to enhance clinical decision-making.",
            "Enable Developers and Regulators: Ensure robust, fair, and compliant AI systems."
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: "Building Explainable AI for Personalized Healthcare"
        },
        {
          type: 'heading',
          level: 3,
          text: "Stakeholder-Centric Explainability:"
        },
        {
          type: 'heading',
          level: 4,
          text: "Patients"
        },
        {
          type: 'paragraph',
          text: "Need: Clear, concise health recommendations."
        },
        {
          type: 'paragraph',
          text: "Solution: Use SHAP (SHapley Additive exPlanations) to highlight the factors influencing AI suggestions, paired with user-friendly summaries."
        },
        {
          type: 'paragraph',
          text: "Example: A patient inputs symptoms, and the AI explains that fatigue and high blood sugar are key indicators of diabetes."
        },
        {
          type: 'heading',
          level: 4,
          text: "Healthcare Providers"
        },
        {
          type: 'paragraph',
          text: "Need: Transparent insights into AI-driven diagnostics."
        },
        {
          type: 'paragraph',
          text: "Solution: Incorporate LIME (Local Interpretable Model-agnostic Explanations) and confidence scores for detailed reasoning."
        },
        {
          type: 'paragraph',
          text: "Example: A doctor reviews an AI-recommended treatment plan with probabilistic confidence levels."
        },
        {
          type: 'heading',
          level: 4,
          text: "Developers and Regulators"
        },
        {
          type: 'paragraph',
          text: "Need: Assurance of fairness and compliance."
        },
        {
          type: 'paragraph',
          text: "Solution: Perform algorithmic audits and provide model cards detailing assumptions and limitations."
        },
        {
          type: 'paragraph',
          text: "Example: A model card explains that the AI was trained on diverse global datasets to minimize bias."
        },
        {
          type: 'heading',
          level: 2,
          text: "Tackling Algorithmic Biases"
        },
        {
          type: 'paragraph',
          text: "Bias in AI can have serious consequences, especially in healthcare. The Personalized Healthcare Assistant adopts the following strategies:"
        },
        {
          type: 'list',
          items: [
            "Logical Fallacies: Tests ensure health advice isn't overgeneralized from biased data.",
            "Foundational Leaks: Stratified sampling addresses incomplete or non-representative training datasets.",
            "GIGO (Garbage In, Garbage Out): Rigorous data preprocessing minimizes errors from noisy inputs."
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: "Privacy, Security, and Environmental Impact"
        },
        {
          type: 'heading',
          level: 3,
          text: "Privacy and Security"
        },
        {
          type: 'paragraph',
          text: "Protecting patient data is paramount. This platform complies with regulations like GDPR and HIPAA by:"
        },
        {
          type: 'list',
          items: [
            "Anonymizing sensitive health data.",
            "Employing end-to-end encryption.",
            "Implementing multi-factor authentication."
          ]
        },
        {
          type: 'heading',
          level: 3,
          text: "Environmental Responsibility"
        },
        {
          type: 'paragraph',
          text: "AI systems can be resource intensive. To reduce the environmental footprint, the platform uses energy-efficient hardware and software, adhering to Green AI principles."
        },
        {
          type: 'heading',
          level: 2,
          text: "Ethical AI Principles"
        },
        {
          type: 'paragraph',
          text: "Ethics are the backbone of responsible AI. The Personalized Healthcare Assistant embodies:"
        },
        {
          type: 'list',
          items: [
            "Fairness: Equitable treatment across all demographics.",
            "Transparency: Clear communication of AI recommendations.",
            "Accountability: Feedback loops for users and defined responsibilities for decision outcomes.",
            "Privacy and Security: Robust data protection measures.",
            "Beneficence: Prioritizing patient safety and well-being."
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: "Future Vision: A Sustainable Healthcare Ecosystem"
        },
        {
          type: 'paragraph',
          text: "As AI continues to evolve, the Personalized Healthcare Assistant sets a benchmark for responsible and transparent healthcare technology. By addressing stakeholder needs, mitigating biases, and upholding ethical principles, this platform is poised to revolutionize personalized medicine."
        },
        {
          type: 'paragraph',
          text: "Imagine a world where healthcare is:"
        },
        {
          type: 'list',
          items: [
            "Accessible: Patients receive tailored advice anytime, anywhere.",
            "Efficient: Providers leverage AI for faster, more accurate decisions.",
            "Equitable: Developers create fair, unbiased models that serve diverse populations."
          ]
        },
        {
          type: 'heading',
          level: 2,
          text: "Final Thoughts"
        },
        {
          type: 'paragraph',
          text: "The journey toward building a trustworthy AI-powered healthcare ecosystem is not without its challenges. Yet, with a focus on explainability, fairness, and ethics, the Personalized Healthcare Assistant demonstrates how technology can bridge the gap between innovation and care."
        }
      ],
      authorBio: {
        name: "Arun Kumar BS, Himanshu Manroa, Jasika Singh, Manoj Dhiman, Sachin Shingari, Sayantan Chakraborty",
        credentials: "Doctoral in Business Administration [Emerging technologies — Gen AI], Golden Gate University [San Francisco]",
        bio: "A team of healthcare technology experts and researchers focused on advancing AI applications in personalized healthcare."
      },
      tags: ["HealthcareAI", "PersonalizedMedicine", "EthicalAI", "DigitalHealth", "AIinHealthcare", "HealthcareInnovation", "MedicalAI", "HealthTech"]
    }
  };

  const blogPost = blogPosts[id];

  if (!blogPost) {
    return (
      <div className="min-h-screen bg-richblue-5 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Article Not Found</h1>
          <Link
            to="/blog"
            className="text-richblue-600 hover:text-richblue-700 transition-colors duration-300"
          >
            Return to Articles
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    return content.map((section, index) => {
      switch (section.type) {
        case 'heading':
          if (section.level === 2) {
            return (
              <h2 key={index} className="text-2xl font-bold text-white mt-12 mb-6">
                {section.text}
              </h2>
            );
          } else if (section.level === 3) {
            return (
              <h3 key={index} className="text-xl font-semibold text-white mt-8 mb-4">
                {section.text}
              </h3>
            );
          } else if (section.level === 4) {
            return (
              <h4 key={index} className="text-lg font-semibold text-white mt-6 mb-3">
                {section.text}
              </h4>
            );
          }
          break;
        case 'paragraph':
          return (
            <p key={index} className="text-richblack-300 mb-6 leading-relaxed text-lg">
              {section.text}
            </p>
          );
        case 'list':
          return (
            <ul key={index} className="list-disc list-inside text-richblack-300 mb-6 space-y-2 text-lg">
              {section.items.map((item, i) => (
                <li key={i} className="ml-4">{item}</li>
              ))}
            </ul>
          );
        default:
          return null;
      }
    });
  };

  return (
    <div className="min-h-screen bg-richblue-5">
      {/* Back Button */}
      <div className="w-11/12 mx-auto pt-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-richblue-500 hover:text-richblue-700 transition-colors duration-300"
        >
          <FaArrowLeft className="text-sm" />
          Back to Articles
        </Link>
      </div>

      {/* Article Header */}
      <div className="w-11/12 max-w-4xl mx-auto mt-8">
        <div className="flex items-center gap-4 text-richblue-500 mb-6">
          <div className="flex items-center gap-2">
            <FaUser className="text-richblue-400" />
            <span>{blogPost.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-richblue-400" />
            <span>{blogPost.date}</span>
          </div>
          <div className="flex items-center gap-2">
            <FaBookmark className="text-richblue-400" />
            <span>{blogPost.readTime}</span>
          </div>
        </div>

        {blogPost.credentials && (
          <p className="text-richblack-300 mb-6 italic">{blogPost.credentials}</p>
        )}

        <h1 className="text-4xl font-bold text-white mb-8 leading-tight">
          {blogPost.title}
        </h1>

        {/* Article Content */}
        <article className="prose prose-lg max-w-none text-richblue-700">
          {renderContent(blogPost.content)}
        </article>

        {/* Tags */}
        {blogPost.tags && (
          <div className="mt-8 flex flex-wrap gap-2">
            {blogPost.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-richblue-5 text-richblue-500 rounded-full text-sm border border-richblue-300"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Author Bio */}
        <div className="mt-12 p-6 bg-richblue-5 rounded-xl border border-richblue-300">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-richblue-100 flex items-center justify-center">
              <FaUser className="text-richblue-400 text-xl" />
            </div>
            <div>
              <h3 className="text-richblue-700 font-semibold">{blogPost.authorBio.name}</h3>
              <p className="text-richblue-500 text-sm">
                {blogPost.authorBio.followers} followers · {blogPost.authorBio.following} following
              </p>
            </div>
          </div>
          <p className="text-richblue-500">{blogPost.authorBio.bio}</p>
        </div>

        {/* Share Section */}
        <div className="mt-8 flex items-center gap-4 text-richblue-500">
          <span>Share this article:</span>
          <button className="p-2 hover:text-richblue-700 transition-colors duration-300">
            <FaShare />
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPost; 